import { useCallback, useEffect, useState } from 'react';
import { App, Input, Modal, Space, Typography } from 'antd';
import { SendOutlined } from '@ant-design/icons';
import {
  extractScreenDiagnostics,
  publishScreenConfig,
  type ScreenConfigView,
  type ScreenDiagnostic,
} from '@/api/screens';
import { normalizeApiError } from '@/api/errors';
import { DiagnosticsPanel } from './DiagnosticsPanel';

const { Text, Paragraph } = Typography;

/**
 * The ONE publish confirmation for screen configs — used from the studio table, the version
 * detail drawer AND the builder itself, so publishing never means dropping to another page. It
 * rides the existing endpoint unchanged: publish re-validates against this hub's content ladder
 * (the full verdict renders inline and the modal stays open), signs Ed25519, freezes the row;
 * the step-up (maker-checker) refusal is surfaced verbatim, never worked around.
 */
export function PublishModal({
  target,
  onClose,
  onPublished,
}: {
  /** The draft to publish; null = closed. */
  target: { configId: string; version: number } | null;
  onClose: () => void;
  onPublished: (view: ScreenConfigView) => void;
}) {
  const { message } = App.useApp();
  const [note, setNote] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [diagnostics, setDiagnostics] = useState<ScreenDiagnostic[] | null>(null);

  // A fresh target is a fresh act: stale notes/verdicts never leak between publishes.
  useEffect(() => {
    setNote('');
    setDiagnostics(null);
  }, [target?.configId, target?.version]);

  const publish = useCallback(async () => {
    if (!target) return;
    setPublishing(true);
    setDiagnostics(null);
    try {
      const view = await publishScreenConfig(target.configId, target.version, note.trim() || undefined);
      message.success(
        `'${view.configId}' v${view.version} PUBLISHED — signed ${view.signatureAlgorithm}, ` +
          `${view.contentHash?.slice(0, 18)}…`,
      );
      onPublished(view);
    } catch (e) {
      const gate = extractScreenDiagnostics(e);
      if (gate) {
        // Publish re-validates against THIS hub's content ladder — a key withdrawn since
        // draft time surfaces here. Show the full verdict, keep the modal open.
        setDiagnostics(gate);
        return;
      }
      const err = normalizeApiError(e);
      if (err.httpStatus === 403 && err.code === 'STEPUP_REQUIRED') {
        message.error(
          'Publishing screens is a step-up lane: re-authenticate with a second factor (loa2) and retry.',
        );
      } else {
        message.error(err.message);
      }
    } finally {
      setPublishing(false);
    }
  }, [target, note, message, onPublished]);

  return (
    <Modal
      title={
        target && (
          <Space>
            <SendOutlined />
            <span>
              Publish <Text className="ol-mono">{target.configId}</Text> v
              {target.version}
            </span>
          </Space>
        )
      }
      open={target !== null}
      onCancel={onClose}
      onOk={() => void publish()}
      okText="Sign and publish"
      confirmLoading={publishing}
    >
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Paragraph style={{ margin: 0, fontSize: 13 }}>
          Publishing re-validates against this hub&apos;s content ladder, signs the canonical
          bytes (Ed25519), freezes the version and makes it the ONE live journey for this config. New
          invites use it from then on; invites already sent keep the journey they were minted from.
          The previous published version is superseded.
        </Paragraph>
        <Input.TextArea
          placeholder="Publish note (optional — lands in the ledger)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          autoSize={{ minRows: 2, maxRows: 4 }}
        />
        {diagnostics && <DiagnosticsPanel diagnostics={diagnostics} />}
      </Space>
    </Modal>
  );
}
